import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

import { UserService } from '../core/services/user.service';

/**
 * uploadRoleGuard — quick-260805-mpl / QA D9 (Srikanth Rev2, 2026-08-05).
 *
 * DEFECT: the standalone Upload nav item is hidden for REVIEWER/VIEWER, but the /upload route
 * only carried `authGuard`. Those roles could still reach the page by direct URL, attempt an
 * upload, and be met with the raw backend string "Missing required X-School-Code header" — a
 * technical internal leaking out where a permission message belongs.
 *
 * FIX: a role-aware guard composed AFTER authGuard (which proves the Keycloak session first).
 * Unauthorized roles get a clean snackbar and a /dashboard UrlTree — the documented Angular
 * redirect pattern, same as the bare '' landing guard in app.routes.ts.
 *
 * Notes:
 *  - PLATFORM_ADMIN is deliberately ALLOWED. It is not school-scoped, so it lands on the
 *    by-design "not provisioned to any school" empty state (QA VAL-004/A2) — that is the
 *    correct, informative outcome, not a permission denial.
 *  - The server-side X-School-Code 400 STAYS as defense in depth. This guard is UX only; it is
 *    not, and must not be treated as, the authorization boundary.
 *  - ADR-0031: roles are API-sourced, so the guard is ASYNC and awaits the memoized
 *    GET /api/users/me bootstrap. Without that await a hard refresh straight onto /upload would
 *    evaluate roles while the profile is still in flight ([] roles) and false-deny an allowed
 *    user. ensureLoaded() is single-flight, so this costs no extra round trip once loaded.
 *  - ALL inject() calls happen BEFORE the first await: Angular's DI context is lost after an
 *    await inside a functional guard.
 */

/** Roles permitted to reach /upload. Everything else — REVIEWER, VIEWER, DATA_STEWARD and any
 *  unknown role — is denied. */
export const UPLOAD_ROLES: readonly string[] = [
  'DISTRICT_ADMIN',
  'SCHOOL_ADMIN',
  'SCHOOL_USER',
  'STATE_ADMIN',
  'PLATFORM_ADMIN',
] as const;

export const uploadRoleGuard: CanActivateFn = async () => {
  const user = inject(UserService);
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);

  await user.ensureLoaded();

  // hasAnyRole is case-insensitive and treats empty/unloaded roles as NOT authorized (ADR-0031).
  if (user.hasAnyRole(...UPLOAD_ROLES)) {
    return true;
  }

  snackBar.open("You don't have permission to upload data.", 'Dismiss', {
    duration: 8000,
    politeness: 'polite',
  });
  return router.parseUrl('/dashboard');
};
